import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { StoreLayout } from "@/components/layout/StoreLayout";
import { motion } from "motion/react";
import { Phone, MapPin, PackageCheck, AlertCircle, ArrowLeft, FileText, Mail, Truck } from "lucide-react";

export default function ReturnsPolicy() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === "ar";

  return (
    <StoreLayout>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className="pt-24 pb-section-gap-desktop max-w-4xl mx-auto px-grid-margin"
      >

        {/* Back link */}
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-label-sm font-label-sm text-on-surface-variant hover:text-primary transition-colors"
          >
            <ArrowLeft className={`w-4 h-4 ${isAr ? "rotate-180" : ""}`} />
            {t("product.back", { defaultValue: isAr ? "العودة للرئيسية" : "Back to Home" })}
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <span className="text-label-sm font-label-sm text-secondary uppercase tracking-[0.2em] mb-3 block">
            {isAr ? "خدمة عملاء سين" : "SEEN Client Care"}
          </span>
          <h1 className="font-display-lg text-[clamp(28px,4.5vw,46px)] text-deep-chocolate mb-4 uppercase tracking-wider">
            {t("returns.title", { defaultValue: isAr ? "شروط الخدمة" : "Terms of Service" })}
          </h1>
          <p className="text-body-lg text-on-surface-variant max-w-2xl mx-auto leading-relaxed">
            {t("returns.subtitle", {
              defaultValue: isAr
                ? "يرجى مراجعة الشروط والأحكام التي تحكم استخدامك لموقع سين وشراء مجموعات الأزياء والملابس لدينا."
                : "Please review the terms and conditions that govern your use of our website and purchase of our clothing collections.",
            })}
          </p>
        </div>

        {/* Terms Sections Document */}
        <div className="bg-surface-container-lowest dark:bg-surface-container-low rounded-3xl border border-outline-variant/40 p-6 sm:p-10 shadow-sm mb-12">
          
          {/* Document Header Badge */}
          <div className="flex items-center gap-2 pb-6 mb-8 border-b border-outline-variant/30 text-xs font-semibold text-secondary uppercase tracking-widest">
            <FileText className="w-4 h-4" />
            <span>{isAr ? "وثيقة الشروط والأحكام الرسمية" : "Official Terms of Service Agreement"}</span>
          </div>

          <div className="space-y-8 text-on-surface-variant leading-relaxed text-sm sm:text-base">

            {/* OVERVIEW */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "نظرة عامة (OVERVIEW)" : "OVERVIEW"}
              </h2>
              <p>
                {isAr
                  ? "يتم تشغيل هذا الموقع بواسطة علامة SEEN (سين) للأزياء في مصر. تشير المصطلحات \"نحن\" و\"لنا\" و\"خاصتنا\" إلى SEEN. تقدم SEEN هذا الموقع، بما في ذلك جميع المعلومات والمجموعات والأدوات والخدمات المتاحة لك، شريطة موافقتك على جميع الشروط والأحكام والسياسات والإشعارات المذكورة هنا."
                  : "This website is operated by SEEN. Throughout the site, the terms “we”, “us” and “our” refer to SEEN. SEEN offers this website, including all information, tools and Services available from this site to you, the user, conditioned upon your acceptance of all terms, conditions, policies and notices stated here."}
              </p>
              <p>
                {isAr
                  ? "من خلال زيارة موقعنا و/أو شراء أي قطعة ملابس منا، فإنك تشترك في \"خدمتنا\" وتوافق على الالتزام بالشروط والأحكام التالية (\"شروط الخدمة\"، \"الشروط\"). تنطبق شروط الخدمة هذه على جميع مستخدمي الموقع."
                  : "By visiting our site and/ or purchasing clothing items from us, you engage in our “Service” and agree to be bound by the following terms and conditions (“Terms of Service”, “Terms”), including those additional terms and conditions and policies referenced herein and/or available by hyperlink. These Terms of Service apply to all users of the site."}
              </p>
              <p>
                {isAr
                  ? "يرجى قراءة شروط الخدمة هذه بعناية قبل الدخول إلى موقعنا أو استخدامه. من خلال الوصول إلى أي جزء من الموقع أو استخدامه، فإنك توافق على الالتزام بشروط الخدمة هذه. إذا لم توافق على جميع الشروط والأحكام الواردة في هذه الاتفاقية، فلا يجوز لك الوصول إلى الموقع أو استخدام أي من خدماتنا."
                  : "Please read these Terms of Service carefully before accessing or using our website. By accessing or using any part of the site, you agree to be bound by these Terms of Service. If you do not agree to all the terms and conditions of this agreement, then you may not access the website or use any Services."}
              </p>
              <p>
                {isAr
                  ? "تخضع أي ميزات أو مجموعات أزياء أو أدوات جديدة تضاف إلى المتجر لشروط الخدمة. نحتفظ بالحق في تحديث أو تغيير أو استبدال أي جزء من شروط الخدمة هذه عن طريق نشر التحديثات على موقعنا."
                  : "Any new features, clothing collections, or tools which are added to the current store shall also be subject to the Terms of Service. You can review the most current version of the Terms of Service at any time on this page. We reserve the right to update, change or replace any part of these Terms of Service by posting updates and/or changes to our website."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 1 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١ - شروط المتجر الإلكتروني (SECTION 1 - ONLINE STORE TERMS)" : "SECTION 1 - ONLINE STORE TERMS"}
              </h2>
              <p>
                {isAr
                  ? "بالموافقة على شروط الخدمة هذه، فإنك تقر بأنك بلغت سن الرشد على الأقل في محل إقامتك، أو أنك بلغت سن الرشد ومنحتنا موافقتك للسماح لأي من المعالين القصر باستخدام هذا الموقع."
                  : "By agreeing to these Terms of Service, you represent that you are at least the age of majority in your state or province of residence, or that you are the age of majority in your state or province of residence and you have given us your consent to allow any of your minor dependents to use this site."}
              </p>
              <p>
                {isAr
                  ? "لا يجوز لك استخدام منتجاتنا أو تصاميمنا لأي غرض غير قانوني أو غير مصرح به، ولا يجوز لك، أثناء استخدام الخدمة، انتهاك أي قوانين في نطاق اختصاصك القضائي (بما في ذلك قوانين حقوق النشر والعلامات التجارية)."
                  : "You may not use our fashion products for any illegal or unauthorized purpose nor may you, in the use of the Service, violate any laws in your jurisdiction (including but not limited to copyright laws)."}
              </p>
              <p>
                {isAr
                  ? "يجب عليك عدم نقل أي فيروسات أو أي برمجيات ذات طبيعة تخريبية. سيؤدي أي انتهاك للشروط إلى الإنهاء الفوري لخدماتك."
                  : "You must not transmit any worms or viruses or any code of a destructive nature. A breach or violation of any of the Terms will result in an immediate termination of your Services."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 2 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ٢ - الشروط العامة (SECTION 2 - GENERAL CONDITIONS)" : "SECTION 2 - GENERAL CONDITIONS"}
              </h2>
              <p>
                {isAr
                  ? "نحتفظ بالحق في رفض الخدمة لأي شخص لأي سبب وفي أي وقت."
                  : "We reserve the right to refuse Service to anyone for any reason at any time."}
              </p>
              <p>
                {isAr
                  ? "تدرك أن محتواك (باستثناء معلومات بطاقات الدفع) قد يتم نقله غير مشفر عبر شبكات مختلفة ليتوافق مع المتطلبات التقنية. يتم دائمًا تشفير معلومات بطاقات الدفع أثناء النقل عبر الشبكات."
                  : "You understand that your content (not including payment card information), may be transferred unencrypted and involve (a) transmissions over various networks; and (b) changes to conform and adapt to technical requirements of connecting networks or devices. Payment card information is always encrypted during transfer over networks."}
              </p>
              <p>
                {isAr
                  ? "توافق على عدم إعادة إنتاج أو نسخ أو بيع أو إعادة بيع أو استغلال أي جزء من الخدمة أو تصاميم الأزياء دون إذن كتابي صريح منا."
                  : "You agree not to reproduce, duplicate, copy, sell, resell or exploit any portion of the Service, use of the Service, or access to the Service or any contact on the website through which the Service is provided, without express written permission by us."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 3 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ٣ - دقة واكتمال وحداثة المعلومات (SECTION 3 - ACCURACY OF INFORMATION)" : "SECTION 3 - ACCURACY, COMPLETENESS AND TIMELINESS OF INFORMATION"}
              </h2>
              <p>
                {isAr
                  ? "نحن غير مسؤولين إذا كانت المعلومات المتاحة على هذا الموقع غير دقيقة أو كاملة أو حديثة. المواد المتوفرة هي للمعلومات العامة فقط. قد يحتوي هذا الموقع على معلومات تاريخية أو مجموعات سابقة تم توفيرها كمرجع فقط."
                  : "We are not responsible if information made available on this site is not accurate, complete or current. The material on this site is provided for general information only and should not be relied upon or used as the sole basis for making decisions without consulting primary, more accurate, more complete or more timely sources of information."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 4 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ٤ - تعديل الخدمات والأسعار (SECTION 4 - MODIFICATIONS TO SERVICE & PRICES)" : "SECTION 4 - MODIFICATIONS TO THE SERVICE AND PRICES"}
              </h2>
              <p>
                {isAr
                  ? "أسعار منتجاتنا قابلة للتغيير دون إشعار مسبق. نحتفظ بالحق في أي وقت في تعديل أو إيقاف الخدمة (أو أي جزء من التشكيلات) دون إشعار مسبق."
                  : "Prices for our products are subject to change without notice. We reserve the right at any time to modify or discontinue the Service (or any part or content thereof) without notice at any time. We shall not be liable to you or to any third-party for any modification, price change, suspension or discontinuance of the Service."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 5 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ٥ - منتجات الأزياء وسياسة الإرجاع (SECTION 5 - PRODUCTS & REFUND POLICY)" : "SECTION 5 - PRODUCTS OR SERVICES (CLOTHING & REFUNDS)"}
              </h2>
              <p>
                {isAr
                  ? "قد تتوفر بعض قطع الأزياء والملابس حصريًا عبر الإنترنت من خلال الموقع. قد تكون هذه المنتجات بكميات محدودة وتخضع للإرجاع أو الاستبدال فقط وفقًا لسياسة الاستبدال والاسترجاع المعتمدة لدينا:"
                  : "Certain products or Services may be available exclusively online through the website. These clothing products may have limited quantities and are subject to return or exchange only according to our Return Policy:"}
              </p>
              <div className="bg-secondary/10 p-4 rounded-xl border border-secondary/20 text-xs sm:text-sm my-2">
                <p className="font-semibold text-primary mb-1">
                  {isAr ? "• سياسة الاستبدال والاسترجاع:" : "• Inspection & Return Policy:"}
                </p>
                <p>
                  {isAr
                    ? "الاستبدال أو الاسترجاع يكون فقط أثناء معاينة وفحص المنتج مع المندوب عند التسليم. والشركة غير مسؤولة بعد استلام المنتج من المندوب إلا للحالات التي تستحق النظر."
                    : "Exchange or return is only accepted during the product inspection with the courier upon delivery. The company is not responsible for any issues once the product is received from the courier, except in special cases that require consideration."}
                </p>
              </div>
              <p>
                {isAr
                  ? "لقد بذلنا كل جهد ممكن لعرض ألوان وتفاصيل وأقمشة منتجاتنا بأعلى دقة ممكنة، ولكن لا يمكننا ضمان دقة عرض شاشتك لأي لون بنسبة 100%."
                  : "We have made every effort to display as accurately as possible the colors and details of our products that appear at the store. We cannot guarantee that your monitor's display of any color will be completely accurate."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 6 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ٦ - دقة الفواتير ومعلومات الحساب (SECTION 6 - BILLING & ACCOUNT ACCURACY)" : "SECTION 6 - ACCURACY OF BILLING AND ACCOUNT INFORMATION"}
              </h2>
              <p>
                {isAr
                  ? "نحتفظ بالحق في رفض أي طلب تضعه معنا. يجوز لنا، وفقًا لتقديرنا الخاص، تحديد أو إلغاء الكميات المشتراة للشخص الواحد أو لكل طلب. توافق على تقديم معلومات شراء وحساب حديثة وكاملة ودقيقة لجميع عمليات الشراء التي تتم في متجرنا."
                  : "We reserve the right to refuse any order you place with us. We may, in our sole discretion, limit or cancel quantities purchased per person, per household or per order. You agree to provide current, complete and accurate purchase and account information for all purchases made at our store."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 7 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ٧ - الأدوات الاختيارية (SECTION 7 - OPTIONAL TOOLS)" : "SECTION 7 - OPTIONAL TOOLS"}
              </h2>
              <p>
                {isAr
                  ? "قد نوفر لك إمكانية الوصول إلى أدوات تابعة لجهات خارجية (مثل أدوات تحديد المقاسات أو الحاسبات) دون أي مراقبة أو تحكم من جانبنا. إن أي استخدام من جانبك لهذه الأدوات الاختيارية يتم على مسؤوليتك الخاصة."
                  : "We may provide you with access to third-party tools over which we neither monitor nor have any control nor input. Any use by you of the optional tools offered through the site is entirely at your own risk and discretion."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 8 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ٨ - روابط الطرف الثالث (SECTION 8 - THIRD-PARTY LINKS)" : "SECTION 8 - THIRD-PARTY LINKS"}
              </h2>
              <p>
                {isAr
                  ? "قد تتضمن بعض المحتويات والمنتجات والخدمات مواد من أطراف ثالثة. نحن غير مسؤولين عن فحص أو تقييم المحتوى أو الدقة ولا نتحمل أي مسؤولية عن أي مواد أو مواقع ويب تابعة لجهات خارجية."
                  : "Certain content, products and Services available via our Service may include materials from third-parties. We are not responsible for examining or evaluating the content or accuracy and we do not warrant and will not have any liability or responsibility for any third-party materials or websites."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 9 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ٩ - تعليقات المستخدمين وملاحظاتهم (SECTION 9 - USER COMMENTS & FEEDBACK)" : "SECTION 9 - USER COMMENTS, FEEDBACK AND OTHER SUBMISSIONS"}
              </h2>
              <p>
                {isAr
                  ? "إذا أرسلت إلينا أفكارًا إبداعية أو اقتراحات أو تعليقات، فإنك توافق على أنه يجوز لنا، في أي وقت وبدون قيود، تعديل ونشر واستخدام هذه التعليقات دون أي التزام بالسرية أو التعويض."
                  : "If, at our request or without a request from us, you send creative ideas, suggestions, proposals, or other materials, you agree that we may, at any time, without restriction, edit, copy, publish, and distribute in any medium any comments that you forward to us."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 10 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١٠ - المعلومات الشخصية (SECTION 10 - PERSONAL INFORMATION)" : "SECTION 10 - PERSONAL INFORMATION"}
              </h2>
              <p>
                {isAr
                  ? "يخضع تقديمك للمعلومات الشخصية من خلال المتجر لمعايير حماية البيانات وسياسة الخصوصية الخاصة بنا."
                  : "Your submission of personal information through the store is governed by our Privacy Policy and data protection standards."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 11 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١١ - الأخطاء والسهو (SECTION 11 - ERRORS & OMISSIONS)" : "SECTION 11 - ERRORS, INACCURACIES AND OMISSIONS"}
              </h2>
              <p>
                {isAr
                  ? "في بعض الأحيان قد تكون هناك معلومات على موقعنا تحتوي على أخطاء مطبعية أو معلومات غير دقيقة قد تتعلق بأوصاف المنتجات، والأسعار، وتوافر المخزون. نحتفظ بالحق في تصحيح أي أخطاء أو تحديث المعلومات أو إلغاء الطلبات إذا كانت هناك أي معلومات غير دقيقة دون إشعار مسبق."
                  : "Occasionally there may be information on our site or in the Service that contains typographical errors, inaccuracies or omissions relating to product descriptions, pricing, promotions, and availability. We reserve the right to correct any errors, inaccuracies or omissions, and to change or update information or cancel orders if any information is inaccurate at any time without prior notice."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 12 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١٢ - الاستخدامات المحظورة (SECTION 12 - PROHIBITED USES)" : "SECTION 12 - PROHIBITED USES"}
              </h2>
              <p>
                {isAr
                  ? "يحظر عليك استخدام الموقع أو محتواه: (أ) لأي غرض غير قانوني؛ (ب) للتحريض على ارتكاب أفعال غير قانونية؛ (ج) لانتهاك حقوق الملكية الفكرية؛ (د) لنقل فيروسات أو برمجيات خبيثة؛ (هـ) للتدخل في ميزات الأمان الخاصة بالخدمة."
                  : "In addition to other prohibitions as set forth in the Terms of Service, you are prohibited from using the site or its content: (a) for any unlawful purpose; (b) to solicit others to perform or participate in any unlawful acts; (c) to infringe upon or violate our intellectual property rights; (d) to upload or transmit viruses or any malicious code; (e) to interfere with or circumvent the security features of the Service."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 13 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١٣ - إخلاء المسؤولية وحدودها (SECTION 13 - LIMITATION OF LIABILITY)" : "SECTION 13 - DISCLAIMER OF WARRANTIES; LIMITATION OF LIABILITY"}
              </h2>
              <p>
                {isAr
                  ? "لا نضمن أن استخدامك للخدمة سيكون دون انقطاع أو في الوقت المناسب أو آمنًا أو خاليًا من الأخطاء. توافق صراحة على أن استخدامك للخدمة أو عدم قدرتك على استخدامها يقع على مسؤوليتك وحدك. يتم تقديم الخدمة وجميع المنتجات المسلمة لك \"كما هي\" و\"كما هي متوفرة\"."
                  : "We do not guarantee, represent or warrant that your use of our Service will be uninterrupted, timely, secure or error-free. You expressly agree that your use of, or inability to use, the Service is at your sole risk. The Service and all products delivered to you through the Service are provided 'as is' and 'as available' for your use."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 14 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١٤ - التعويض (SECTION 14 - INDEMNIFICATION)" : "SECTION 14 - INDEMNIFICATION"}
              </h2>
              <p>
                {isAr
                  ? "أنت توافق على تعويض وحماية SEEN ومسؤوليها ومديريها وموظفيها من أي مطالبة أو دعوى، بما في ذلك أتعاب المحاماة المعقولة، تنشأ عن خرقك لشروط الخدمة هذه."
                  : "You agree to indemnify, defend and hold harmless SEEN and our directors, officers, employees, and affiliates from any claim or demand, including reasonable attorneys’ fees, made by any third-party due to or arising out of your breach of these Terms of Service."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 15 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١٥ - قابلية الفصل (SECTION 15 - SEVERABILITY)" : "SECTION 15 - SEVERABILITY"}
              </h2>
              <p>
                {isAr
                  ? "في حالة تحديد أي حكم من أحكام شروط الخدمة هذه بأنه غير قانوني أو باطل أو غير قابل للتنفيذ، فإن هذا الحكم يظل قابلاً للتنفيذ إلى أقصى حد يسمح به القانون، ولا يؤثر ذلك على صحة ونفاذ باقي الأحكام."
                  : "In the event that any provision of these Terms of Service is determined to be unlawful, void or unenforceable, such provision shall nonetheless be enforceable to the fullest extent permitted by applicable law, and the unenforceable portion shall be deemed to be severed from these Terms of Service."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 16 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١٦ - إنهاء الخدمة (SECTION 16 - TERMINATION)" : "SECTION 16 - TERMINATION"}
              </h2>
              <p>
                {isAr
                  ? "تظل شروط الخدمة هذه سارية المفعول ما لم يتم إنهاؤها من قبلك أو من قبلنا. يمكنك إنهاء شروط الخدمة هذه في أي وقت عن طريق إخطارنا بأنك لم تعد ترغب في استخدام خدماتنا أو عند التوقف عن استخدام موقعنا."
                  : "These Terms of Service are effective unless and until terminated by either you or us. You may terminate these Terms of Service at any time by notifying us that you no longer wish to use our Services, or when you cease using our site."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 17 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١٧ - مجمل الاتفاق (SECTION 17 - ENTIRE AGREEMENT)" : "SECTION 17 - ENTIRE AGREEMENT"}
              </h2>
              <p>
                {isAr
                  ? "تشكل شروط الخدمة هذه وأي سياسات أو قواعد تشغيل منشورة من قبلنا على هذا الموقع كامل الاتفاق والتفاهم بينك وبيننا وتحكم استخدامك للخدمة."
                  : "These Terms of Service and any policies or operating rules posted by us on this site constitute the entire agreement and understanding between you and us and govern your use of the Service."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 18 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١٨ - القانون الحاكم (SECTION 18 - GOVERNING LAW)" : "SECTION 18 - GOVERNING LAW"}
              </h2>
              <p>
                {isAr
                  ? "تخضع شروط الخدمة هذه وأي اتفاقيات منفصلة نقدم بموجبها الخدمات لك وتفسر وفقًا لقوانين جمهورية مصر العربية."
                  : "These Terms of Service and any separate agreements whereby we provide you Services shall be governed by and construed in accordance with the laws of Egypt."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 19 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ١٩ - التغييرات في شروط الخدمة (SECTION 19 - CHANGES TO TERMS)" : "SECTION 19 - CHANGES TO TERMS OF SERVICE"}
              </h2>
              <p>
                {isAr
                  ? "يمكنك مراجعة أحدث إصدار من شروط الخدمة في أي وقت على هذه الصفحة. نحتفظ بالحق، وفقًا لتقديرنا الخاص، في تحديث أو تغيير أو استبدال أي جزء من هذه الشروط."
                  : "You can review the most current version of the Terms of Service at any time at this page. We reserve the right, at our sole discretion, to update, change or replace any part of these Terms of Service by posting updates and changes to our website."}
              </p>
            </section>

            <hr className="border-outline-variant/20" />

            {/* SECTION 20 */}
            <section className="space-y-3">
              <h2 className="text-base sm:text-lg font-bold text-primary uppercase tracking-wide">
                {isAr ? "القسم ٢٠ - معلومات التواصل (SECTION 20 - CONTACT INFORMATION)" : "SECTION 20 - CONTACT INFORMATION"}
              </h2>
              <p>
                {isAr
                  ? "يجب إرسال الأسئلة المتعلقة بشروط الخدمة إلينا عبر معلومات الاتصال الموضحة أدناه:"
                  : "Questions about the Terms of Service should be sent to us using our contact details below:"}
              </p>
              
              <div className="bg-surface-container-low rounded-2xl p-5 border border-outline-variant/40 space-y-2.5 mt-3">
                <p className="font-bold text-primary">SEEN Egypt</p>
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <MapPin className="w-4 h-4 text-secondary shrink-0" />
                  <span>{t("returns.address", { defaultValue: isAr ? "الجيزه البدرشين شارع بور سعيد" : "Giza, Al Badrashin, Port Said Street" })}</span>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <Phone className="w-4 h-4 text-secondary shrink-0" />
                  <a href={`tel:${t("returns.phone", { defaultValue: "01558783669" })}`} className="hover:text-primary transition-colors">
                    {t("returns.phone", { defaultValue: "01558783669" })}
                  </a>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <Mail className="w-4 h-4 text-secondary shrink-0" />
                  <a href="mailto:seen@optima-software-solutions.com" className="hover:text-primary transition-colors">
                    seen@optima-software-solutions.com
                  </a>
                </div>
              </div>
            </section>

          </div>
        </div>

        {/* Shipping Policy Section & Highlights Header */}
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <div className="inline-flex items-center justify-center gap-2.5 mb-3 text-secondary font-semibold text-xs sm:text-sm uppercase tracking-[0.25em]">
            <Truck className="w-5 h-5 text-secondary" />
            <span>{isAr ? "خدمات الشحن والتوصيل" : "Shipping & Delivery"}</span>
          </div>
          <h2 className="font-display-lg text-[clamp(26px,4vw,38px)] text-deep-chocolate mb-4 uppercase tracking-wider font-bold">
            {isAr ? "سياسة الشحن والمعاينة" : "Shipping Policy"}
          </h2>
          <p className="text-body-lg text-on-surface-variant leading-relaxed text-sm sm:text-base">
            {isAr
              ? "نلتزم في SEEN بتقديم تجربة شحن استثنائية وسريعة لكافة المحافظات في جمهورية مصر العربية. يتم تجهيز كل قطعة بعناية فائقة وتغليفها وفق أرقى معايير الجودة. لضمان ثقتكم ورضاكم التام، نوفر خدمة المعاينة المباشرة مع مندوب الشحن لفحص جودة ومقاسات مشترياتكم قبل استلام الطلب."
              : "At SEEN, we are dedicated to providing a swift, seamless, and premium delivery experience across all governorates of Egypt. Every order is meticulously tailored, inspected, and packaged to the highest standards. To guarantee complete peace of mind, we offer a dedicated doorstep inspection service with our courier so you can review your items before final receipt."}
          </p>
        </div>

        {/* Returns & Inspection Highlight Cards (After Full Terms of Service Card) */}
        <div className="grid sm:grid-cols-2 gap-5 mb-12">
          <div className="bg-surface-container-low rounded-2xl p-6 sm:p-7 border border-outline-variant/30 flex gap-4 shadow-sm">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
              <PackageCheck className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-headline-md text-base sm:text-lg text-primary mb-1.5 font-bold">
                {t("returns.inspectionTitle", { defaultValue: isAr ? "الفحص عند التسليم" : "Inspection at Delivery" })}
              </h3>
              <p className="text-body-md text-sm text-on-surface-variant leading-relaxed">
                {t("returns.policy1", {
                  defaultValue: isAr
                    ? "الاستبدال او الاسترجاع يكون فقط خلال معاينه المنتج مع المندوب."
                    : "Exchange or return is only accepted during the product inspection with the courier.",
                })}
              </p>
            </div>
          </div>

          <div className="bg-surface-container-low rounded-2xl p-6 sm:p-7 border border-outline-variant/30 flex gap-4 shadow-sm">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-primary/5 flex items-center justify-center text-primary">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-headline-md text-base sm:text-lg text-primary mb-1.5 font-bold">
                {t("returns.afterDeliveryTitle", { defaultValue: isAr ? "بعد الاستلام" : "After Delivery" })}
              </h3>
              <p className="text-body-md text-sm text-on-surface-variant leading-relaxed">
                {t("returns.policy2", {
                  defaultValue: isAr
                    ? "وان الشركه غير مسؤله بعد استلام المنتج من المندوب الا للحالات التي تستحق النظر."
                    : "The company is not responsible for any issues once the product is received from the courier, except in special cases that require consideration.",
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Contact Footer Box */}
        <div className="bg-surface-container-low rounded-2xl border border-outline-variant/30 p-8 sm:p-10 text-center shadow-sm">
          <h2 className="font-display text-2xl text-deep-chocolate mb-2">
            {t("returns.contactTitle", { defaultValue: isAr ? "للمزيد من الأسئلة؟" : "Still have questions?" })}
          </h2>
          <p className="text-body-md text-on-surface-variant mb-8 max-w-md mx-auto">
            {t("returns.contactDesc", { defaultValue: isAr ? "من فضلك تواصل معنا، نحن هنا لمساعدتك." : "For more inquiries, please don't hesitate to reach out to us." })}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 items-center justify-center gap-8">
            {/* Phone */}
            <div className="flex flex-col items-center gap-2.5">
              <div className="w-11 h-11 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                <Phone className="w-5 h-5" />
              </div>
              <span className="text-label-sm uppercase tracking-widest text-on-surface-variant font-semibold text-xs">
                {t("returns.callUs", { defaultValue: isAr ? "اتصل بنا" : "Call Us" })}
              </span>
              <a
                href={`tel:${t("returns.phone", { defaultValue: "01558783669" })}`}
                className="font-headline-sm text-base sm:text-lg text-primary hover:text-secondary transition-colors"
              >
                {t("returns.phone", { defaultValue: "01558783669" })}
              </a>
            </div>

            {/* Email */}
            <div className="flex flex-col items-center gap-2.5">
              <div className="w-11 h-11 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                <Mail className="w-5 h-5" />
              </div>
              <span className="text-label-sm uppercase tracking-widest text-on-surface-variant font-semibold text-xs">
                {isAr ? "راسلنا" : "Email Us"}
              </span>
              <a
                href="mailto:seen@optima-software-solutions.com"
                className="font-headline-sm text-xs sm:text-sm text-primary hover:text-secondary transition-colors break-all"
              >
                seen@optima-software-solutions.com
              </a>
            </div>

            {/* Address */}
            <div className="flex flex-col items-center gap-2.5">
              <div className="w-11 h-11 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                <MapPin className="w-5 h-5" />
              </div>
              <span className="text-label-sm uppercase tracking-widest text-on-surface-variant font-semibold text-xs">
                {t("returns.visitUs", { defaultValue: isAr ? "زورنا في" : "Visit Us" })}
              </span>
              <span className="font-headline-sm text-xs sm:text-sm text-primary text-center leading-snug max-w-xs">
                {t("returns.address", { defaultValue: isAr ? "الجيزه البدرشين شارع بور سعيد" : "Giza, Al Badrashin, Port Said Street" })}
              </span>
            </div>
          </div>
        </div>

      </motion.div>
    </StoreLayout>
  );
}

